import React from 'react';
import { Button, ButtonGroup, Card, Div, Group, Header, Panel, Progress, SimpleCell, Text, Title } from '@vkontakte/vkui';
import { Icon28CheckCircleOutline, Icon28ErrorCircleOutline, Icon28ListCheckOutline } from '@vkontakte/icons';
import { AppShell, ScreenHeader } from '../../components/Layout/AppShell';
import { EmptyState, LoadingRing } from '../../components/States/States';
import { TRAINING_PANEL } from '../../routing/routing';
import {
  trainingEmptyStateText,
  trainingPromptLabel,
  trainingResultSummary,
  trainingSourceLabel
} from '../../utils/text';

function optionAppearance(option, selectedTrainingOption, trainingFeedbackAnswer) {
  if (!trainingFeedbackAnswer) {
    return { appearance: 'accent', mode: 'secondary' };
  }

  const isSelected = option.option === selectedTrainingOption;

  if (option.is_correct) {
    return { appearance: 'positive', mode: 'secondary' };
  }

  if (isSelected) {
    return { appearance: 'negative', mode: 'secondary' };
  }

  return { appearance: 'neutral', mode: 'secondary' };
}

function resultStatus(percent) {
  if (percent < 40) {
    return { title: 'Нужно повторить', icon: <Icon28ErrorCircleOutline />, appearance: 'negative' };
  }

  if (percent < 90) {
    return { title: 'Хороший прогресс', icon: <Icon28ListCheckOutline />, appearance: 'accent' };
  }

  return { title: 'Почти идеально', icon: <Icon28CheckCircleOutline />, appearance: 'positive' };
}

export function TrainingPanel({
  nav = TRAINING_PANEL,
  training,
  currentTrainingIndex,
  currentTrainingItem,
  loading,
  selectedTrainingOption,
  trainingFeedbackPhase,
  trainingFeedbackAnswer,
  isSubmittingTraining,
  trainingSubmitError,
  onBack,
  answerTraining,
  advanceTraining,
  onRestart,
  onRetrySubmit
}) {
  const progress = training.items.length > 0
    ? Math.round(((currentTrainingIndex + (currentTrainingItem ? 1 : 0)) / training.items.length) * 100)
    : 0;
  const correctPercent = training.items.length > 0
    ? Math.round((training.correct / training.items.length) * 100)
    : 0;
  const mistakes = training.answers.filter((item) => !item.correct);
  const status = resultStatus(correctPercent);

  return (
    <Panel nav={nav}>
      <ScreenHeader title="Тренировка слов" onBack={onBack} />
      <AppShell>
        {!training.done && currentTrainingItem ? (
          <>
            <Group>
              <Div>
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <Text className="text-[var(--vkui--color_text_secondary)]">{trainingSourceLabel(training.source)}</Text>
                    <Title level="2" className="mt-1">
                      {currentTrainingIndex + 1} из {training.items.length}
                    </Title>
                  </div>
                  <Text>{progress}%</Text>
                </div>
                <Progress value={progress} />
                <Text className="mt-4 text-[var(--vkui--color_text_secondary)]">
                  {trainingPromptLabel(currentTrainingItem.translation_type)}
                </Text>
                <Title level="1" className="mt-2 break-words">{currentTrainingItem.word}</Title>
                {currentTrainingItem.translation_type !== 'ru-en' ? (
                  <Text className="mt-2 text-[var(--vkui--color_text_secondary)]">[{currentTrainingItem.transcription}]</Text>
                ) : null}
              </Div>
            </Group>

            <Group header={<Header>Один ответ верный</Header>}>
              <Div className="space-y-3">
                {currentTrainingItem.options.map((option) => {
                  const tone = optionAppearance(option, selectedTrainingOption, trainingFeedbackAnswer);

                  return (
                    <Button
                      key={option.option}
                      size="l"
                      stretched
                      align="left"
                      mode={tone.mode}
                      appearance={tone.appearance}
                      onClick={() => answerTraining(option)}
                      disabled={trainingFeedbackPhase !== 'idle' || isSubmittingTraining}
                    >
                      <span className="block break-words whitespace-normal text-left">{option.option}</span>
                    </Button>
                  );
                })}

                {trainingFeedbackAnswer ? (
                  <Text className={trainingFeedbackAnswer.correct ? 'text-[var(--vkui--color_text_positive)]' : 'text-[var(--vkui--color_text_negative)]'}>
                    {trainingFeedbackAnswer.correct
                      ? 'Верно. Можно переходить к следующему слову.'
                      : `Правильный ответ: ${trainingFeedbackAnswer.correctOption}`}
                  </Text>
                ) : (
                  <Text className="text-[var(--vkui--color_text_secondary)]">Выбирайте вариант одним нажатием.</Text>
                )}

                {trainingFeedbackAnswer ? (
                  <Button size="l" stretched onClick={advanceTraining}>
                    {currentTrainingIndex + 1 === training.items.length ? 'Завершить тренировку' : 'Далее'}
                  </Button>
                ) : null}
              </Div>
            </Group>
          </>
        ) : null}

        {!training.done && !currentTrainingItem && training.items.length > 0 ? (
          <Group>
            <Div className="text-center">
              {isSubmittingTraining ? (
                <>
                  <Title level="2">Сохраняем результат</Title>
                  <Text className="mt-2 text-[var(--vkui--color_text_secondary)]">Подводим итоги тренировки и обновляем ваш прогресс.</Text>
                  <div className="my-5"><LoadingRing /></div>
                </>
              ) : (
                <>
                  <Title level="2">Не удалось завершить тренировку</Title>
                  <Text className="mt-2 text-[var(--vkui--color_text_secondary)]">{trainingSubmitError || 'Попробуйте отправить результат ещё раз.'}</Text>
                  <ButtonGroup mode="vertical" stretched className="mt-5">
                    <Button size="l" stretched onClick={onRetrySubmit}>Повторить отправку</Button>
                    <Button size="l" mode="secondary" stretched onClick={onRestart}>Начать заново</Button>
                  </ButtonGroup>
                </>
              )}
            </Div>
          </Group>
        ) : null}

        {training.done ? (
          <>
            <Group>
              <SimpleCell before={status.icon} subtitle={trainingResultSummary(training.correct, training.items.length)} multiline>
                {status.title}
              </SimpleCell>
              <Div>
                <Title level="1">{correctPercent}%</Title>
                <Text className="text-[var(--vkui--color_text_secondary)]">{training.correct} из {training.items.length} верно</Text>
                <Progress value={correctPercent} appearance={status.appearance} className="mt-3" />
              </Div>
            </Group>

            <Group header={<Header>{mistakes.length > 0 ? 'Что стоит повторить' : 'Ошибок не было'}</Header>}>
              {mistakes.length > 0 ? (
                <Div className="space-y-2">
                  {mistakes.map((item) => (
                    <Card key={item.key} mode="outline">
                      <Div>
                        <Text className="text-[var(--vkui--color_text_secondary)]">
                          {item.translationType === 'ru-en' ? 'Русский → английский' : 'Английский → русский'}
                        </Text>
                        <Title level="3" className="mt-1 break-words">{item.prompt}</Title>
                        <Text className="mt-2 text-[var(--vkui--color_text_negative)]">Ваш ответ: {item.option}</Text>
                        <Text className="mt-1 text-[var(--vkui--color_text_positive)]">Правильный ответ: {item.correctOption}</Text>
                      </Div>
                    </Card>
                  ))}
                </Div>
              ) : (
                <Div>
                  <Text>Можно переходить к новой подборке слов.</Text>
                </Div>
              )}

              {training.unlocked.length > 0 ? (
                <SimpleCell before={<Icon28CheckCircleOutline />} subtitle={`${training.unlocked.length} достижений`}>
                  Открыто достижений
                </SimpleCell>
              ) : null}
            </Group>

            <Group>
              <Div>
                <ButtonGroup mode="vertical" stretched>
                  <Button size="l" stretched onClick={onRestart}>Пройти ещё раз</Button>
                  <Button size="l" mode="secondary" stretched onClick={onBack}>Вернуться назад</Button>
                </ButtonGroup>
              </Div>
            </Group>
          </>
        ) : null}

        {!loading && training.items.length === 0 ? (
          <EmptyState text={trainingEmptyStateText(training.source)} />
        ) : null}
      </AppShell>
    </Panel>
  );
}
